import { FormProvider, UseFormReturn, useForm } from 'react-hook-form'

import { Button } from '@factorialco/deprecated-design-system'
import { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'

import ModalComponent from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  InMemoryAdditionalCompensation,
  useContractFlow,
} from 'modules/contracts/lib/contractFlowContext'

import { AdditionalCompensationFormFields } from '../AdditionalCompensationForm'

type EditAdditionalCompensationFormProps = EditAdditionalCompensationFormModalProps & {
  useFormMethods: UseFormReturn<InMemoryAdditionalCompensation>
  payrollConcepts: PayrollConceptSelectFieldFragment[]
}
function EditAdditionalCompensationForm({
  useFormMethods,
  payrollConcepts,
  ...additionalCompensationFormFields
}: EditAdditionalCompensationFormProps) {
  return (
    <FormProvider {...useFormMethods}>
      <AdditionalCompensationFormFields
        payrollConcepts={payrollConcepts}
        {...additionalCompensationFormFields}
      />
    </FormProvider>
  )
}

type EditAdditionalCompensationFormModalProps = {
  additionalCompensation: InMemoryAdditionalCompensation
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  onClose?: () => void
  onEdited?: () => void
  isEditable?: boolean
}

function EditAdditionalCompensationFormModal({
  onClose,
  additionalCompensation,
  payrollConcepts,
  ...editAdditionalCompensationForm
}: EditAdditionalCompensationFormModalProps) {
  const methods = useForm<InMemoryAdditionalCompensation>({ defaultValues: additionalCompensation })
  const { additionalCompensations, setAdditionalCompensations } = useContractFlow()

  const onSubmitForm = async () => {
    const isValid = await methods.trigger()

    if (!isValid) {
      return
    }

    const editedAdditionalCompensation = methods.getValues()

    track(trackingEvents.compensations.updateCompensation, {
      type: 'contract',
      compensationId: editedAdditionalCompensation.id || editedAdditionalCompensation.tmpID,
    })

    const compareId = (
      a: InMemoryAdditionalCompensation,
      b: InMemoryAdditionalCompensation
    ): boolean => {
      // If using tmpID (frontend generated)
      if (a.tmpID && b.tmpID) {
        return a.tmpID === b.tmpID
      }
      // If using id (backend generated)
      if (a.id && b.id) {
        return a.id === b.id
      }
      return false
    }

    const newCompensations = additionalCompensations.map((ac) =>
      compareId(ac, editedAdditionalCompensation) ? { ...editedAdditionalCompensation } : ac
    )

    setAdditionalCompensations(newCompensations)
    onClose && onClose()
  }
  return (
    <ModalComponent
      illustration="edit-file-filled"
      title={
        editAdditionalCompensationForm.isEditable
          ? i18n.t(
              'contract_versions.additional_compensation.edit_additional_compensation_form_modal.title'
            )
          : i18n.t(
              'contract_versions.additional_compensation.read_additional_compensation_form_modal.title'
            )
      }
      description={
        editAdditionalCompensationForm.isEditable
          ? i18n.t(
              'contract_versions.additional_compensation.edit_additional_compensation_form_modal.description'
            )
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <Button
            type={editAdditionalCompensationForm.isEditable ? 'secondary' : 'primary'}
            onClick={onClose}
          >
            {editAdditionalCompensationForm.isEditable
              ? i18n.t(
                  'contract_versions.additional_compensation.edit_additional_compensation_form_modal.close_text'
                )
              : i18n.t(
                  'contract_versions.additional_compensation.read_additional_compensation_form_modal.close_text'
                )}
          </Button>
          {editAdditionalCompensationForm.isEditable && (
            <Button disabled={!methods.formState.isValid} onClick={onSubmitForm}>
              {i18n.t(
                'contract_versions.additional_compensation.edit_additional_compensation_form_modal.save'
              )}
            </Button>
          )}
        </>
      }
    >
      <EditAdditionalCompensationForm
        additionalCompensation={additionalCompensation}
        useFormMethods={methods}
        payrollConcepts={payrollConcepts}
        {...editAdditionalCompensationForm}
      />
    </ModalComponent>
  )
}

export { EditAdditionalCompensationFormModal }
